import { useEffect, useState } from "react";
import axios from "axios";
import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, BarChart3, Globe2, Activity, Award, RefreshCw, Eye } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
// Token jest w localStorage["epidemos-token"] i AuthContext ustawia axios.defaults.headers.common globalnie
// — wystarczy zwykły axios.get bez explicit headers
const H = () => ({});

export default function SeoDashboard() {
  const [overview, setOverview] = useState(null);
  const [gsc, setGsc] = useState(null);
  const [worst, setWorst] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rescoringSite, setRescoringSite] = useState(null);
  const [tab, setTab] = useState("overview");

  const load = async () => {
    setLoading(true);
    try {
      const [o, g, w] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/seo-dashboard/overview`, { headers: H() }),
        axios.get(`${BACKEND_URL}/api/admin/seo-dashboard/gsc`, { headers: H() }).catch(() => ({ data: { metrics: [] } })),
        axios.get(`${BACKEND_URL}/api/admin/quality/worst?limit=30`, { headers: H() }).catch(() => ({ data: { posts: [] } })),
      ]);
      setOverview(o.data);
      setGsc(g.data);
      setWorst(w.data.posts || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "SEO Dashboard | Epidemos Admin";
    load();
  }, []);

  const bulkRescore = async () => {
    setRescoringSite("all");
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/quality/bulk-score?limit=5000&save=true`, {}, { headers: H() });
      alert(`Scored ${r.data.scored} posts.\nRozkład: ${JSON.stringify(r.data.distribution)}`);
      load();
    } finally {
      setRescoringSite(null);
    }
  };

  if (loading) return <div className="p-8 text-center text-slate-500">Ładuję dashboard…</div>;
  if (!overview) return <div className="p-8 text-red-500">Błąd ładowania</div>;

  const TrendIcon = ({ pct }) => {
    if (pct >= 5) return <TrendingUp className="w-4 h-4 text-green-600" />;
    if (pct <= -5) return <TrendingDown className="w-4 h-4 text-red-600" />;
    return <span className="text-slate-400 text-xs">→</span>;
  };

  const StatCard = ({ icon: Icon, label, value, sub, color = "blue" }) => (
    <div className={`p-4 bg-white rounded-xl border border-${color}-100 shadow-sm`}>
      <div className="flex items-center justify-between mb-1">
        <Icon className={`w-5 h-5 text-${color}-600`} />
        <span className="text-xs text-slate-400">{label}</span>
      </div>
      <div className="text-2xl font-bold text-slate-900">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );

  return (
    <div className="p-6 max-w-none mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-blue-600" /> SEO Dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-1">Audyt agentów, GSC trendy, jakość treści — w jednym miejscu</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg hover:bg-slate-50 flex items-center gap-1.5">
            <RefreshCw className="w-4 h-4" /> Odśwież
          </button>
          <button onClick={bulkRescore} disabled={rescoringSite}
            className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1.5 disabled:opacity-50">
            <Award className="w-4 h-4" /> {rescoringSite ? "Scoring…" : "Wymuś rescore 5000"}
          </button>
        </div>
      </div>

      {/* Topline stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Globe2} label="Sites active" value={overview.sites.active}
          sub={`${overview.sites.configs_enabled} z agentem włączonym`} color="blue" />
        <StatCard icon={Activity} label="Publikacje 7d" value={overview.publikacje["7d"].toLocaleString()}
          sub={`${overview.publikacje["30d"].toLocaleString()} / 30d · ${overview.publikacje.total.toLocaleString()} total`} color="green" />
        <StatCard icon={CheckCircle2} label="Agent success 7d" value={`${overview.agent.success_rate}%`}
          sub={`${overview.agent.completed_7d}/${overview.agent.runs_7d} runs · ${overview.agent.failed_7d} failed`} color="purple" />
        <StatCard icon={AlertTriangle} label="Alerty otwarte" value={overview.alerts_open}
          sub={overview.alerts_open === 0 ? "wszystko gra" : "wymaga uwagi"}
          color={overview.alerts_open > 0 ? "amber" : "green"} />
      </div>

      {/* Quality status */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-slate-900 flex items-center gap-2">
            <Award className="w-5 h-5 text-purple-600" /> Quality Score (last 2000 postów)
          </h2>
          <div className="text-sm text-slate-500">
            scored: <strong>{overview.quality.scored.toLocaleString()}</strong>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="font-bold text-red-700 text-lg">{overview.quality.weak_lt40}</div>
            <div className="text-xs text-red-600">posts &lt;40 (do rewrite)</div>
          </div>
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="font-bold text-green-700 text-lg">{overview.quality.strong_gte75}</div>
            <div className="text-xs text-green-600">posts ≥75 (jakość OK)</div>
          </div>
          <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
            <div className="font-bold text-slate-700 text-lg">{overview.quality.scored - overview.quality.weak_lt40 - overview.quality.strong_gte75}</div>
            <div className="text-xs text-slate-600">posts 40-74 (średnio)</div>
          </div>
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="font-bold text-blue-700 text-lg">
              {Math.round(overview.quality.strong_gte75 / overview.quality.scored * 100)}%
            </div>
            <div className="text-xs text-blue-600">% z dobrym scorem</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-4 flex gap-1">
        {[
          { id: "overview", label: "Top portale", icon: Globe2 },
          { id: "gsc", label: `GSC (${gsc?.metrics?.length || 0})`, icon: TrendingUp },
          { id: "worst", label: `Słabe posty (${worst.length})`, icon: AlertTriangle },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition flex items-center gap-1.5 ${
              tab === t.id ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-900"
            }`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "overview" && (
        <div className="bg-white rounded-xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 uppercase text-xs">
              <tr>
                <th className="text-left p-3">#</th>
                <th className="text-left p-3">Portal</th>
                <th className="text-right p-3">Posty 30d</th>
                <th className="text-right p-3">~ Dziennie</th>
                <th className="text-left p-3">URL</th>
              </tr>
            </thead>
            <tbody>
              {overview.top_portals_30d.map((p, i) => (
                <tr key={p.site_id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="p-3 text-slate-400">{i + 1}</td>
                  <td className="p-3 font-medium">{p.name}</td>
                  <td className="p-3 text-right font-mono font-semibold">{p.posts_30d}</td>
                  <td className="p-3 text-right text-slate-500">{Math.round(p.posts_30d / 30)}</td>
                  <td className="p-3"><a href={p.url} target="_blank" rel="noopener" className="text-blue-600 hover:underline text-xs">{p.url?.replace(/^https?:\/\/(www\.)?/, "")}</a></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "gsc" && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          {!gsc?.metrics?.length ? (
            <div className="p-6 text-center bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="w-8 h-8 text-amber-600 mx-auto mb-2" />
              <p className="font-semibold text-amber-900">perf_cache jest pusty</p>
              <p className="text-sm text-amber-800 mt-1">GSC integration nie produkuje danych. OAuth token może być wygasły — sprawdź .env GOOGLE_OAUTH_*.</p>
              <p className="text-xs text-amber-700 mt-2">Last update: <code>{gsc?.last_update || "?"}</code></p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs">
                <tr>
                  <th className="text-left p-2">Portal</th>
                  <th className="text-right p-2">Clicks 14d</th>
                  <th className="text-right p-2">Prev 14d</th>
                  <th className="text-right p-2">Trend</th>
                  <th className="text-right p-2">Position</th>
                  <th className="text-right p-2">Impressions</th>
                </tr>
              </thead>
              <tbody>
                {gsc.metrics.map(m => (
                  <tr key={m.site_id} className="border-t border-slate-100">
                    <td className="p-2">{m.name}</td>
                    <td className="p-2 text-right font-mono">{m.clicks_now || 0}</td>
                    <td className="p-2 text-right font-mono text-slate-500">{m.clicks_prev || 0}</td>
                    <td className="p-2 text-right">
                      <span className="inline-flex items-center gap-1">
                        <TrendIcon pct={m.trend_pct} />
                        {m.trend_pct > 0 ? "+" : ""}{m.trend_pct}%
                      </span>
                    </td>
                    <td className="p-2 text-right font-mono">{(m.position || 0).toFixed(1)}</td>
                    <td className="p-2 text-right text-slate-500">{m.impressions_now || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "worst" && (
        <div className="bg-white rounded-xl border border-slate-200">
          {worst.length === 0 ? (
            <div className="p-6 text-center text-slate-500">Brak słabych postów (score &lt; 50)</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs">
                <tr>
                  <th className="text-right p-2 w-16">Score</th>
                  <th className="text-left p-2">Tytuł</th>
                  <th className="text-left p-2">Portal</th>
                  <th className="text-left p-2">Issues</th>
                  <th className="p-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {worst.map(p => (
                  <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="p-2 text-right">
                      <span className={`inline-block px-2 py-0.5 rounded font-bold text-white ${p.quality_score < 25 ? "bg-red-500" : p.quality_score < 50 ? "bg-amber-500" : "bg-slate-400"}`}>
                        {p.quality_score}
                      </span>
                    </td>
                    <td className="p-2 font-medium text-slate-900">{p.title?.substring(0, 70)}</td>
                    <td className="p-2 text-slate-600">{p.site_name}</td>
                    <td className="p-2 text-xs text-slate-500">{(p.quality_issues || []).slice(0, 3).join("; ")}</td>
                    <td className="p-2">
                      {p.published_url && (
                        <a href={p.published_url} target="_blank" rel="noopener" className="text-blue-600">
                          <Eye className="w-4 h-4" />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
